import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getSession } from 'services/session/sessionService';
import { SessionResponse, transformObjectResponse } from 'services/session/types';
import { languageMessages } from 'traductor/FirstPayment';

interface UseSessionDataResult {
  token: string;
  sessionData: SessionResponse | null;
  objectData: any;
  isLoaded: boolean;
  isVantage: boolean;
  error: { title: string; subtitle: string; buttonText: string } | null;
  handleError: (type: string) => void;
}

export function useSessionData(language: string, region: string, changeLanguage: (lang: string) => void): UseSessionDataResult {
  const location = useLocation();
  const [token, setToken] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [sessionData, setSessionData] = useState<SessionResponse | null>(null);
  const [objectData, setObjectData] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [isVantage, setIsVantage] = useState(false);

  const hasFetched = useRef(false);

  const handleError = useCallback((type: string) => {
    const errorInfo = languageMessages(language, "errorMessages")[type];
    setError({
      title: errorInfo.title,
      subtitle: errorInfo.subtitle,
      buttonText: errorInfo.buttonText,
    });
    console.warn(type, errorInfo.subtitle);
  }, [language]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const sessionToken = queryParams.get('session') ?? '';
    const originVantage = queryParams.get('origin') ?? '';

    setToken(sessionToken);
    setIsVantage(originVantage === 'app-mobile');

    if (!sessionToken) {
      handleError('session');
      return;
    }

    hasFetched.current = true;

    const fetchSession = async () => {
      if (objectData) return; // Prevent multiple fetches
      try {
        const session: SessionResponse = await getSession(sessionToken, region);
        if (session.subscriptionHash) {
          handleError('existsSession');
          return;
        }

        setSessionData(session);
        const transformed = transformObjectResponse(session, region);
        const languageMap: { [key: string]: string } = {
          EN: 'en',
          US: 'en',
          SP: 'es',
          PT: 'pt',
          ES: 'es',
        };
        changeLanguage(languageMap[session.language]);
        setObjectData(transformed);
      } catch (err) {
        handleError('session');
        console.error('session', JSON.stringify(err));
      } finally {
        setIsLoaded(true);
      }
    };

    fetchSession();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, changeLanguage, handleError]);

  return { token, sessionData, objectData, isLoaded, isVantage, error, handleError };
}
